import HeaderSection from '@/components/Header/HeaderSection.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { assistant_panel_mixin } from '@/components/BolideAssistant/assistant_panel_state';
import { get_token, get_user_profile } from '@/router';
import {
  main_dashboard_id,
  default_dashboard_menu_key,
  role_dashboards
} from '@/config/dashboardIds';
import { ROLE_TO_APP_KEY } from '@/data/sidebar_config';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';

// Environment variables
const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;
const superset_sdk_url = process.env.VUE_APP_SUPERSET_SDK_URL;

// Superset Config
const SUPERSET_CONFIG = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: main_dashboard_id
  }
};

const AUTH_CONFIG = {
  guestTokenUrl: guest_token_url
};

const MAIN_CONTAINER_ID = 'dashboard-main';
const MAIN_BREADCRUMB = { id: 'main', title: 'Dashboard' };

// Token Cache
const TOKEN_CACHE = {
  tokens: new Map(),

  get(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && Date.now() < cached.expiresAt) return cached.token;
    if (cached) this.tokens.delete(dashboardId);
    return null;
  },

  set(dashboardId, token, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() { this.tokens.clear(); },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt) this.tokens.delete(dashboardId);
    }
  }
};

function safeParseMaybeDoubleEncodedJSON(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

export default {
  name: 'DashboardView',

  mixins: [assistant_panel_mixin],

  components: {
    HeaderSection,
    PortfolioSuperSetChart,
    PortfolioSidebar,
    BolideAssistant,
    BolideLoader
  },

  data() {
    return ({
      active_menu_item: default_dashboard_menu_key,
      role_key: 'fund_manager',

      breadcrumb: [],

      iframeLoading: false,
      isMobile: false,

      authToken: null,
      tokenRefreshInProgress: false,
      sidebar_collapsed: false,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false
    });
  },

  computed: {
    current_dashboard_id() {
      const dashboardMap = role_dashboards[this.role_key] || role_dashboards.fund_manager;
      return dashboardMap[this.active_menu_item] || dashboardMap.default || main_dashboard_id;
    }
  },


  mounted() {
    this.set_role_dashboards();

    this.is_loading = true;
    this.loader_start_time = Date.now();  // Record loader start moment

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    // Fallback if superset never loads
    setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000);

    // 5️⃣ Setup responsiveness
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // 6️⃣ Set initial breadcrumb
    this.breadcrumb = [MAIN_BREADCRUMB];

    // 7️⃣ Token cleanup
    this.tokenCleanupInterval = setInterval(() => {
      TOKEN_CACHE.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    set_role_dashboards() {
      const normalizedRole = this.get_normalized_role();
      const appKey = ROLE_TO_APP_KEY[String(normalizedRole)] || normalizedRole;
      this.role_key = role_dashboards[String(appKey)] ? appKey : 'fund_manager';

      const dashboardMap = role_dashboards[this.role_key] || role_dashboards.fund_manager;
      if (!dashboardMap[this.active_menu_item]) {
        const firstKey = Object.keys(dashboardMap).find(key => key !== 'default');
        this.active_menu_item = firstKey || default_dashboard_menu_key;
      }
    },

    get_normalized_role() {
      const user = get_user_profile() || {};
      const roleCandidates = [
        user.role_key,
        user.role,
        user.user_role?.role?.name,
        user.user_role?.role_name,
        user.profile_role,
        sessionStorage.getItem('role')
      ];

      const role = roleCandidates.find(value =>
        value !== null && value !== undefined && String(value).trim() !== ''
      );

      return String(role || '').trim().toLowerCase().replace(/\s+/g, '_');
    },
    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handleResize() {
      this.isMobile = window.innerWidth < 992;
      this.is_mobile = window.innerWidth < 768;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },
    // ========== DASHBOARD INITIALIZATION ==========
    async initializeDashboard() {
      try {
        this.iframeLoading = true;
        this.authToken = get_token();

        if (!this.authToken) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();
        await this.embedDashboard(MAIN_CONTAINER_ID, this.current_dashboard_id);

        // Superset is ready → hide loader
        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          // Show loader for at least min time
          this.is_loading = false;
        } else {
          // Add delay to complete minimum duration
          const remaining = this.min_loader_time - elapsed;
          setTimeout(() => {
            this.is_loading = false;
          }, remaining);
        }
      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframeLoading = false;
      }
    },

    async fetchGuestToken(dashboardId = null, { allowMainDashboardFallback = false } = {}) {
      if (this.tokenRefreshInProgress) {
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.tokenRefreshInProgress) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        const cachedToken = TOKEN_CACHE.get(dashboardId);
        if (cachedToken) return { token: cachedToken };
      }

      this.tokenRefreshInProgress = true;

      try {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;
        const cachedToken = TOKEN_CACHE.get(targetDashboardId);
        if (cachedToken) return { token: cachedToken };

        const response = await fetch(AUTH_CONFIG.guestTokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${this.authToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'aicampus_vsi' })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Guest token request failed: ${response.status}, ${errorText}`);
        }

        const rawResponse = await response.text();
        const data = safeParseMaybeDoubleEncodedJSON(rawResponse);

        if (data.status === 'success' && data.data && data.data.token) {
          TOKEN_CACHE.set(targetDashboardId, data.data.token);
          return data.data;
        } else {
          throw new Error(data.message || 'Failed to get guest token');
        }
      } catch (error) {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;

        if (allowMainDashboardFallback &&
          targetDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main) {
          this.tokenRefreshInProgress = false;
          return this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main);
        }

        throw new Error(`Failed to fetch guest token: ${error.message}`);
      } finally {
        this.tokenRefreshInProgress = false;
      }
    },

    async initializeSupersetSDK() {
      if (typeof window.supersetEmbeddedSdk === 'undefined') await this.loadSupersetSDK();
      return true;
    },

    loadSupersetSDK() {
      return new Promise((resolve, reject) => {
        if (typeof window.supersetEmbeddedSdk !== 'undefined') {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = superset_sdk_url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Superset SDK'));
        document.head.appendChild(script);
      });
    },

    async configureSupersetSDK() {
      if (!window.supersetEmbeddedSdk) throw new Error('Superset SDK not loaded');
    },

    // ========== DASHBOARD EMBEDDING ==========
    async embedDashboard(containerId, dashboardId, forceRefreshToken = true) {
      try {
        this.iframeLoading = true;

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        // Clear cached token if forcing refresh
        if (forceRefreshToken) {
          TOKEN_CACHE.tokens.delete(dashboardId);
        }

        const guestTokenData = await this.fetchGuestToken(dashboardId);

        if (!window.supersetEmbeddedSdk) {
          await this.initializeSupersetSDK();
          await this.configureSupersetSDK();
        }

        await window.supersetEmbeddedSdk.embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_CONFIG.baseUrl,
          mountPoint: mountPoint,
          fetchGuestToken: async () => {
            const tokenData = await this.fetchGuestToken(dashboardId, {
              allowMainDashboardFallback: true
            });
            return tokenData.token || guestTokenData.token;
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: false, expanded: false }
          }
        });

        // Wait until Superset iframe signals it's ready
        window.addEventListener("message", event => {
          if (event.data?.type === "dashboardLoadComplete" ||
            event.data?.event === "dashboard_loaded") {

            // Hide loader if still visible
            if (this.is_loading) {
              this.is_loading = false;
            }
          }
        });

        this.iframeLoading = false;
      } catch (error) {
        console.error('Failed to embed dashboard:', error);
        this.iframeLoading = false;
      }
    },

    cleanupDashboards() {
      const containers = [MAIN_CONTAINER_ID];
      containers.forEach(containerId => {
        const container = document.getElementById(containerId);
        if (container) container.innerHTML = '';
      });
    },

    // ========== NAVIGATION METHODS ==========
    handleBreadcrumbClick(breadcrumbId) {
      if (breadcrumbId === 'main') {
        this.handleGlobalMenuSelected(default_dashboard_menu_key);
      }
    },

    async goBack() {
      this.breadcrumb = [MAIN_BREADCRUMB];
      this.cleanupDashboards();
      this.active_menu_item = default_dashboard_menu_key;

      TOKEN_CACHE.clear();

      this.$nextTick(async () => {
        try {
          await this.embedDashboard(MAIN_CONTAINER_ID, this.current_dashboard_id);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    async handleGlobalMenuSelected(data) {
      const menuItem = typeof data === 'string' ? data : data.key;
      const label = typeof data === 'object' ? data.label : null;

      this.active_menu_item = menuItem;

      TOKEN_CACHE.cleanup();

      if (menuItem === default_dashboard_menu_key) {
        this.breadcrumb = [MAIN_BREADCRUMB];
      } else if (label) {
        this.breadcrumb = [
          MAIN_BREADCRUMB,
          { id: menuItem, title: label }
        ];
      }

      this.$nextTick(async () => {
        try {
          await this.embedDashboard(MAIN_CONTAINER_ID, this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load global dashboard:', error);
        }
      });
    },

    getMainContentClass() {
      if (this.isMobile) return 'col-12';
      return 'col-lg-12 col-md-12 m-0 p-0';
    }
  }
};
